import {
  Vector3,
  MathUtils,
  PerspectiveCamera,
  Object3D,
  Box3,
  Vector2
} from "three";
import linspace from "exact-linspace";
import isUrl from "is-url";
import * as olColor from 'ol/color';
import { Viewer } from "../main";

export const isMobile = /android|webos|iphone|ipad|ipod|blackberry|playbook|BB10|iemobile|opera mini/i.test(navigator.userAgent.toLowerCase());

export const isValidURL = (path: string) => {
  return isUrl(encodeURI(path));
};

export const getProjFactor = (fov, distance, screenHeight) => {
  let projFactor = 1.0 / Math.tan(fov / 2);
  projFactor /= distance;
  projFactor *= screenHeight / 2;
  return projFactor;
};

export const getPointScale = (camera: PerspectiveCamera, mesh: Object3D, size=50) => {
  const meshPosition = new Vector3();
  mesh.getWorldPosition(meshPosition);

  const fov = MathUtils.degToRad(Math.max(20, camera.fov));
  const distance = Math.max(1.0, meshPosition.distanceTo(camera.position));
  const projFactor = getProjFactor(fov, distance, window.innerHeight);
  return size / projFactor;
};

export const toVector2 = (vector: Vector3) => {
  return new Vector2(vector.x, vector.y);
};

export const toScreenPosition = (viewer: Viewer, position: Vector3) => {
  var uv = new Vector3();
  uv.set(position.x, position.y, position.z);
  uv.project(viewer.camera);

  uv.x = (uv.x + 1) * (viewer.width / 2);
  uv.y = (-uv.y + 1) * (viewer.height / 2);

  uv.x = Math.round(uv.x);
  uv.y = Math.round(uv.y);
  uv.z = 0;

  return uv;
};

export const eventToPixel = (event) => {
  let uv = new Vector3();

  if (event.offsetX) {
    // chrome,ie,safari
    uv.x = event.offsetX;
    uv.y = event.offsetY;
  } else {
    // firefox
    uv.x = event.layerX;
    uv.y = event.layerY;
  }

  return uv;
};

export const randomHexColor = () => {
  return "#000000".replace(/0/g, function() {
    return (~~(Math.random() * 16)).toString(16);
  });
};

export const closestPowerOfTwo = value => {
  if (value === 0) {
    return 0;
  }

  let power = Math.ceil(Math.log(value) / Math.log(2));
  let result =  Math.pow(2, power) - 1;
  return result;
};

export const roundDigit = (num, digits) => {
  let multiplyFactor = Math.pow(10, digits);
  return Math.round( num * multiplyFactor + Number.EPSILON ) / multiplyFactor;
};

export const executeWithoutLogging = callback => {
  const logger = console.log;
  console.log = function() {};
  let response = callback();
  console.log = logger;
  return response;
};

export const windowSafeDateISO = () => {
  const date = new Date();
  return date.toISOString()
    .slice(0, 19)
    .replace('T', ' ')
    .replace(/:/g, "-");
};

export const chunkedArray = (array, size) => {
  let tmp = [...array];
  let results = [];
  while (tmp.length) {
    results.push(tmp.splice(0, size));
  }
  return results;
};

export const coordinatesToArray = (points) => {
  let numPoints = points.length / 2;
  let newPoints = [];
  for (let i=0;i<numPoints;i++) {
    let x = points[2*i];
    let y = points[2*i + 1];
    newPoints.push([x, y]);
  }

  return newPoints;
};

export const interpolate1D = (start, end, steps) => {
  if (start === end) {
    return new Array(steps).fill(start);
  }

  return linspace(start, end, steps);
};

export const toBoolean = (value: string | number) : boolean => {
  const integerValue = typeof value === "number"
    ? value
    : parseInt(value);

  return !!integerValue;
};

export const mergeBoundingBoxes = (boundingBoxes: Box3[]) => {
  let combined: Box3 = null;

  boundingBoxes.forEach(bbox => {
    if (!bbox) return;

    if (combined === null) {
      combined = new Box3(
        bbox.min.clone(),
        bbox.max.clone()
      );
    } else {
      combined = new Box3(
        new Vector3(
          Math.min(combined.min.x, bbox.min.x),
          Math.min(combined.min.y, bbox.min.y),
          Math.min(combined.min.z, bbox.min.z)
        ),
        new Vector3(
          Math.max(combined.max.x, bbox.max.x),
          Math.max(combined.max.y, bbox.max.y),
          Math.max(combined.max.z, bbox.max.z)
        )
      );
    }
  });

  return combined;
};


export const getCoordinateBounds = (coordinates: number[][]) => {
  let min = [Infinity, Infinity, Infinity];
  let max = [-Infinity, -Infinity, -Infinity];

  coordinates.forEach(coordinate => {
    min[0] = Math.min(min[0], coordinate[0]);
    min[1] = Math.min(min[1], coordinate[1]);
    min[2] = Math.min(min[2], coordinate[2]);

    max[0] = Math.max(max[0], coordinate[0]);
    max[1] = Math.max(max[1], coordinate[1]);
    max[2] = Math.max(max[2], coordinate[2]);
  });

  return {
    min: new Vector3().fromArray(min),
    max: new Vector3().fromArray(max)
  };
};


export const allIndexOf = (array, item) => {
  let index = array.indexOf(item);
  let indices = [];

  while (index !== -1) {
    indices.push(index);
    index = array.indexOf(item, ++index);
  }

  return indices;
};

export const withOpacity = (color: string, opacity: number) => {
  let colorWithOpacity = olColor.asArray(color).slice();
  colorWithOpacity[3] = opacity;
  return colorWithOpacity;
};
